import React, { useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { CARE_GAP_CATEGORIES } from "./helper";
import { useUserData } from "components/customHooks/userDataContext";
import { addPatientCareGap } from "apiHandler/actions";

const PAYERS = [
  { label: "UHC", color: "#f97316" },
  { label: "Humana", color: "#06b6d4" },
  { label: "Cigna", color: "#f97316" },
  { label: "BCBS", color: "#3b82f6" },
  { label: "MVP", color: "#10b981" },
  { label: "Aetna", color: "#8b5cf6" },
  { label: "Anthem", color: "#1d4ed8" },
  { label: "Centene", color: "#0ea5e9" },
  { label: "Molina", color: "#0284c7" },
];

const flattenOptions = CARE_GAP_CATEGORIES.flatMap((cat) =>
  cat.items.map((item) => ({ label: item, category: cat.category })),
);

const CareGaps = ({ rowData, fetchData }) => {
  const [source, setSource] = useState("ai");
  const [selectedPayers, setSelectedPayers] = useState([]);
  const [selectedGap, setSelectedGap] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error" | "warning" | "info"
  });

  const userData = useUserData();
  const token = userData?.access_token;

  const togglePayer = (payer) => {
    setSelectedPayers((prev) =>
      prev.includes(payer) ? prev.filter((p) => p !== payer) : [...prev, payer],
    );
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAdd = () => {
    if (source === "payer" && selectedPayers.length === 0) {
      showSnackbar("Select at least one payer", "warning");
      return;
    }

    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!details.trim()) {
      showSnackbar("Please enter care gap details", "warning");
      return;
    }

    const payload = {
      practice_id: rowData.practice_id,
      patient_id: rowData.patient_id,
      care_gap_category: selectedGap?.category,
      care_gap_name: selectedGap?.label,
      status: "New",
      care_gap_detials: details,
      user_name: userData.first_name + " " + userData.last_name,
      source: source === "ai" ? "Scribe" : selectedPayers.join(", "),
    };

    try {
      setSaving(true);

      const res = await addPatientCareGap(payload, token);

      if (res?.status === "Success") {
        showSnackbar("Care gap added successfully", "success");

        fetchData();
        setOpenDialog(false);
        setDetails("");
        setSelectedGap(null);
        setSelectedPayers([]);
      } else {
        showSnackbar("Failed to add care gap", "error");
      }
    } catch (error) {
      console.log("error: ", error);
      showSnackbar("Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const isAddDisabled =
    !selectedGap || (source === "payer" && selectedPayers.length === 0);

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        background: "#f8fafc",
      }}
    >
      {/* Header */}
      <Typography fontWeight={600} fontSize={18} mb={1} color="#14b8a6">
        + Care Gaps
      </Typography>

      <Typography fontSize={13} color="#6b7280" mb={2}>
        Select a gap, choose{" "}
        <Box component="span" fontWeight={600} color="#111827">
          AI & Scribe
        </Box>{" "}
        or{" "}
        <Box component="span" fontWeight={600} color="#111827">
          payer / VBC
        </Box>
        .
      </Typography>

      {/* Source */}
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Typography fontSize={14} fontWeight={500}>
          Source
        </Typography>

        <TextField
          select
          size="small"
          value={source}
          onChange={(e) => {
            const value = e.target.value;

            setSource(value);

            if (value === "ai") {
              setSelectedPayers([]);
            }
          }}
          sx={{
            minWidth: 260,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              background: "#fff",
            },
          }}
          SelectProps={{ native: true }}
        >
          <option value="payer">Payer / VBC (select plans)</option>
          <option value="ai">AI & Scribe</option>
        </TextField>
        <Typography fontSize={12} color="#9ca3af">
          Tied to quality or value-based programs for the selected payer(s).
        </Typography>
      </Stack>

      {/* Payer Section */}
      {source === "payer" && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            background: "#fff",
            mb: 2,
          }}
        >
          <Typography fontSize={13} fontWeight={500} mb={1}>
            Payer / plan (select one or more)
          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={1}>
            {PAYERS.map((payer) => {
              const selected = selectedPayers.includes(payer.label);

              return (
                <Box
                  key={payer.label}
                  onClick={() => togglePayer(payer.label)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.2,
                    py: 0.4,
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: selected ? "#e11d48" : "#e5e7eb",
                    background: selected ? "#eff6ff" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <Chip
                    label={payer.label[0]}
                    size="small"
                    sx={{
                      height: 22,
                      background: payer.color,
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                  <Typography fontSize={13}>{payer.label}</Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Care Gap + Add */}
      <Stack direction="row" spacing={1}>
        <Autocomplete
          fullWidth
          size="small"
          options={flattenOptions}
          groupBy={(o) => o.category}
          getOptionLabel={(o) => o.label}
          onChange={(e, val) => setSelectedGap(val)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select a care gap..." />
          )}
        />

        <Button
          onClick={handleAdd}
          variant="outlined"
          disabled={isAddDisabled}
          color="primary"
          sx={{
            minWidth: 90,
            textTransform: "none",
            borderRadius: "10px",
            borderColor: "#d1d5db",
            "&:hover": {
              borderColor: "#9ca3af",
            },
          }}
        >
          + ADD
        </Button>
      </Stack>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Care Gap Details</DialogTitle>

        <DialogContent>
          <Typography fontSize={13} mb={1}>
            {selectedGap?.label}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter care gap details..."
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CareGaps;
